import AuthLayout from '@/components/auth/AuthLayout';
import FloatingLabelInput from '@/components/FloatingLabelInput';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import SubmitButton from '@/components/SubmitButton';
import useAutoFocus from '@/hooks/useAutoFocus';
import { CloseOutlined, EyeInvisibleOutlined, EyeOutlined, LockOutlined } from '@ant-design/icons';
import { signIn } from 'next-auth/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import styles from './SignIn.module.scss';

const SignIn = () => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [sessionExpiredVisible, setSessionExpiredVisible] = useState(false);

  // Auto-focus email field on mount
  useAutoFocus('email', isLoading);

  // Handle OAuth errors and other messages
  useEffect(() => {
    const { message, error } = router.query;

    // Handle session expiration
    if (message === 'session_expired') {
      setSessionExpiredVisible(true);

      // Clean up URL immediately
      router.replace('/auth/signin', undefined, { shallow: true });
    }

    // Handle OAuth errors
    if (error) {
      let errorMessage = '';
      let errorTitle = 'Sign In Error';

      switch (error) {
        case 'OAuthAccountNotLinked':
          errorTitle = 'Account Already Exists';
          errorMessage =
            'An account with this email already exists. Please sign in with your email and password instead. If you forgot your password, use the "Forgot your password?" link below.';
          break;
        case 'OAuthSignin':
        case 'OAuthCallback':
          errorMessage =
            'There was a problem connecting to Google. Please try again or sign in with your email and password.';
          break;
        case 'OAuthCreateAccount':
          errorMessage =
            'Unable to create your account with Google. Please try signing up with your email address instead.';
          break;
        case 'CredentialsSignin':
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          break;
        default:
          errorMessage = 'An error occurred during sign in. Please try again.';
      }

      toast.error(
        <div>
          <strong>{errorTitle}</strong>
          <div style={{ marginTop: '4px', fontSize: '14px' }}>{errorMessage}</div>
        </div>,
        {
          duration: 8000,
          position: 'top-center',
          style: {
            maxWidth: '500px',
          },
        }
      );

      // Clean up URL after showing error
      router.replace('/auth/signin', undefined, { shallow: true });
    }
  }, [router.query.message, router.query.error]);

  // Dismiss session expired alert on user interaction
  useEffect(() => {
    if (!sessionExpiredVisible) return;

    const dismissAlert = () => setSessionExpiredVisible(false);

    // Dismiss only on actual user interaction (click or keydown)
    // No auto-dismiss timeout - stays visible until user interacts or clicks X
    const events = ['click', 'keydown'];
    events.forEach((event) => document.addEventListener(event, dismissAlert, { once: true }));

    return () => {
      events.forEach((event) => document.removeEventListener(event, dismissAlert));
    };
  }, [sessionExpiredVisible]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCredentialsSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await signIn('credentials', {
        email: formData.email,
        password: formData.password,
        redirect: false,
      });

      if (result?.error) {
        toast.error(result.error);
        setIsLoading(false);
      } else {
        router.push('/account/dashboard');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      toast.error('Unable to sign in');
      setIsLoading(false);
    }
  };

  const isFormValid = formData.email && formData.password;

  // Brand rail content
  const brandContent = {
    headline: "Your Brand's Trust Journey Continues.",
    subheadline: 'Reap the benefits of AI-powered confidence and verified identity.',
    features: [
      'See your logo in every inbox, backed by verified trust.',
      'Stay ahead of threats with continuous AI-driven protection.',
      'Gain even deeper insights with your personalized domain dashboard.',
      'Enjoy effortless, automated compliance and secure brand delivery.',
    ],
  };

  return (
    <AuthLayout
      headline={brandContent.headline}
      subheadline={brandContent.subheadline}
      features={brandContent.features}
      variant="1"
    >
      <div className={styles.authFormContainer}>
        <div className={styles.formHeader}>
          <h1 className={styles.formTitle}>Welcome Back!</h1>
          <p className={styles.formSubtitle}>Sign in to continue protecting your brand.</p>
        </div>

        {/* Session Expired Alert */}
        {sessionExpiredVisible && (
          <div className={styles.sessionExpiredAlert} role="alert">
            <LockOutlined className={styles.alertIcon} />
            <div className={styles.alertContent}>
              <strong>Session Expired</strong>
              <p>
                Your session has expired for security reasons. Your cart items are safely saved.
              </p>
            </div>
            <button
              onClick={() => setSessionExpiredVisible(false)}
              className={styles.alertDismiss}
              aria-label="Dismiss"
            >
              <CloseOutlined />
            </button>
          </div>
        )}

        <div className={styles.formBody}>
          {/* Google Sign In */}
          <GoogleSignInButton disabled={isLoading} mode="signin" />

          <div className={styles.authDivider}>
            <span>or</span>
          </div>

          {/* Credentials Form */}
          <form onSubmit={handleCredentialsSignIn}>
            <FloatingLabelInput
              id="email"
              name="email"
              type="email"
              label="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isLoading}
              autoComplete="email"
            />

            <div style={{ position: 'relative', marginBottom: '24px' }}>
              <FloatingLabelInput
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: isLoading ? '#ccc' : '#666',
                  fontSize: '18px',
                  zIndex: 10,
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
            </div>

            <SubmitButton isLoading={isLoading} disabled={!isFormValid} className="width-100">
              Sign In
              <span className="line-rounded-icon link-icon-right"></span>
            </SubmitButton>
          </form>

          {/* Footer Links */}
          <div className={styles.formFooter}>
            <div className={styles.footerLink}>
              Don't have an account? <Link href="/auth/signup">Sign Up</Link>
            </div>
            <div className={styles.footerLink}>
              <Link href="/forgot-password">Forgot your password?</Link>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default SignIn;
